import { useEffect, useState } from "react";
import { ACTIVE_ALARM_STATES, ALARM_STATE, normalizeAlarmState } from "@lms/shared";

import { Icon } from "./Icon.js";
import type { Cluster } from "./ClusterCard.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";

/**
 * I riquadri sopra il video, portati da `FocusMode.qml`.
 *
 * Sono la parte della schermata che chiede qualcosa all'operatore, e per
 * questo stanno **sopra l'immagine** e non in fondo a una lista: chi guarda un
 * impianto guarda il video, e un allarme che aspetta conferma in un pannello
 * piu' in basso e' un allarme che aspetta a lungo.
 *
 * Quattro stati, ognuno con la sua faccia:
 *
 *  - **fuori servizio**: grigio, senza bottoni. Non c'e' niente da
 *    confermare, e va detto perche' e da chi.
 *  - **UNACK**: rosso o arancione secondo la soglia superata, con la
 *    concentrazione e il limite scritti accanto — confermare senza sapere di
 *    quanto si e' sopra non e' una decisione.
 *  - **RTNUN**: blu. Il gas e' rientrato ma nessuno ha confermato: e' la
 *    situazione che si dimentica piu' facilmente, ed e' esattamente quella in
 *    cui l'allarme e' gia' successo.
 *  - **confermato**: una pillola che sparisce da sola dopo cinque secondi.
 *
 * Sull'ultimo punto: il `.qml` ha due strade per dirlo, un riquadro legato
 * allo stato e un avviso a tempo dopo il clic. Qui c'e' solo il secondo — e'
 * quello che l'operatore vede davvero, e il primo si sovrappone al riquadro
 * di allarme che sta sparendo.
 *
 * Nessuno di questi appare se gli allarmi sono disarmati sull'impianto: un
 * riquadro rosso su un sistema disarmato prometterebbe una sorveglianza che
 * non c'e'.
 */

export interface AlarmRow {
  id: string;
  clusterId: string;
  level: string;
  state: string;
  priority?: string | null;
  ppmValue: number | null;
  createdAt: string;
  acknowledgedBy: string | null;
  /** Dov'era puntata la testa quando l'allarme e' scattato. Nulli sui rig
   *  senza encoder: li' il "vai alla perdita" non ha dove andare. */
  ptzPan?: number | null;
  ptzTilt?: number | null;
  ptzZoom?: number | null;
}

/** Quanto resta a vista la pillola "confermato". */
const ACKED_DISMISS_MS = 5000;
/** Silenziamento di un allarme, in minuti: lo stesso dell'app desktop. */
const SHELVE_MINUTES = 30;

export function AlarmBanner({
  cluster,
  alarms,
  status,
  livePpm,
  alarmsEnabled,
  onChanged,
}: {
  cluster: Cluster;
  /** Gli allarmi di QUESTO impianto, dal piu' recente. */
  alarms: AlarmRow[];
  status: string;
  livePpm: number | undefined;
  alarmsEnabled: boolean;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const { api, can } = useSession();

  const [dismissedRtnun, setDismissedRtnun] = useState("");
  const [justAcked, setJustAcked] = useState(false);
  const [error, setError] = useState("");

  const active = alarms.find((a) => ACTIVE_STATES.has(a.state));
  const state = active === undefined ? undefined : normalizeAlarmState(active.state);

  // La pillola "confermato" si toglie da sola.
  useEffect(() => {
    if (!justAcked) return undefined;
    const timer = setTimeout(() => setJustAcked(false), ACKED_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [justAcked]);

  const canAck = can("acknowledgeAlarms");

  const act = async (route: "alarms.acknowledge" | "alarms.shelve", body: unknown) => {
    try {
      await api.call(route, body);
      setError("");
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const acknowledge = (id: string) => {
    if (state === ALARM_STATE.rtnun) setDismissedRtnun(id);
    setJustAcked(true);
    void act("alarms.acknowledge", { id, user: cluster.name });
  };

  const shelve = (id: string) =>
    void act("alarms.shelve", { id, duration: SHELVE_MINUTES, reason: "Operator shelved" });

  if (cluster.isOutOfService) {
    return (
      <div className="banners">
        <div className="banner banner--oos">
          <span className="banner__icon">
            <Icon name="ban" size={22} />
          </span>
          <div className="banner__text">
            <h3>{t("alarm.outOfService")}</h3>
            <p>
              {[
                cluster.outOfServiceReason
                  ? `${t("alarm.oosReason")}: ${cluster.outOfServiceReason}`
                  : "",
                cluster.outOfServiceBy ? `${t("alarm.oosBy")}: ${cluster.outOfServiceBy}` : "",
              ]
                .filter(Boolean)
                .join(" • ")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const ackedPill = justAcked ? (
    <div className="banners">
      <span className="banner__pill">
        <Icon name="check-circle" size={14} />
        {t("focus.alarmAcked")}
      </span>
    </div>
  ) : null;

  if (!alarmsEnabled || !active) return ackedPill;

  // Il colore e la soglia citata vengono dal LIVELLO dell'allarme, non dallo
  // stato corrente dell'impianto. Nel `.qml` si guarda lo stato, e li' i due
  // coincidono perche' quello stato non diventa mai OFFLINE; qui invece un
  // sensore che ha smesso di rispondere dopo lo scatto lo farebbe, e un
  // allarme L2 verrebbe mostrato in arancione con il limite L1 accanto.
  const critical = active.level === "L2" || (active.level !== "L1" && status === "CRITICAL");
  const limit = critical ? cluster.thresholdL2 : cluster.thresholdL1;

  if (
    state === ALARM_STATE.unack ||
    (state === ALARM_STATE.rtnun && active.id !== dismissedRtnun)
  ) {
    const rtnun = state === ALARM_STATE.rtnun;
    return (
      <div className="banners">
        {error && <p className="alert">{error}</p>}
        <div
          className={`banner banner--${rtnun ? "rtnun" : critical ? "critical" : "warning"}`}
        >
          <span className="banner__icon banner__icon--pulse">
            <Icon
              name={rtnun ? "arrow-down-circle" : critical ? "shield-alert" : "alert-triangle"}
              size={22}
            />
          </span>
          <div className="banner__text">
            <h3>
              {rtnun
                ? t("alarm.state.RTNUN")
                : critical
                  ? t("focus.criticalLeak")
                  : t("focus.warningThreshold")}
            </h3>
            <p>
              {t("focus.concentrationAt")} {Math.round(livePpm ?? 0)} ppm·m
              {!rtnun && ` (L${critical ? "2" : "1"} ${t("focus.limit")}: ${limit ?? "—"})`}
            </p>
            <div className="banner__tags">
              <span className="tag">{t(`alarm.state.${rtnun ? "RTNUN" : "UNACK"}`)}</span>
              {active.priority && (
                <span className={`tag tag--${active.priority.toLowerCase()}`}>
                  {t(`alarm.priority.${active.priority}`)}
                </span>
              )}
            </div>
          </div>
          {canAck && !rtnun && (
            <button className="banner__sq" onClick={() => shelve(active.id)}>
              <Icon name="archive" size={18} />
            </button>
          )}
          {canAck && (
            <button
              className={`banner__ack banner__ack--${rtnun ? "rtnun" : critical ? "critical" : "warning"}`}
              onClick={() => acknowledge(active.id)}
            >
              {t("focus.acknowledge")}
            </button>
          )}
        </div>
      </div>
    );
  }

  return ackedPill;
}

/** Gli stati che meritano un riquadro. Gli altri sono storia. */
const ACTIVE_STATES = new Set(ACTIVE_ALARM_STATES);
